import React, { Suspense, lazy } from 'react';
import { Switch, Route } from 'react-router-dom';
import { Router } from 'react-router';
import { createBrowserHistory, createHashHistory } from 'history';
import Layout from './components/Layout/Layout';
import MainEmbedOptions from './pages/MainEmbedOptions/MainEmbedOptions';
import UxInteractivity from './pages/UxInteractivity/UxInteractivity';
import UiThemes from './pages/UiThemes/UiThemes';
import CrossVisualFiltering from './pages/CrossVisualFiltering/CrossVisualFiltering';
import EmbedApi from './pages/EmbedApi/EmbedApi';
import EventListeners from './pages/EventListeners/EventListeners';
import NewAccount from './pages/NewAccount/NewAccount';
import Landing from './pages/Landing/Landing';
import MySamples from './pages/MySamples/MySamples';
import Sample from './pages/Sample/Sample';
import { NAME_OF_DEFAULT_FILTER_DASHBOARD } from './shared/constants/embedComponentTypes';

const TableauEmbedSamples = lazy(() => import('./pages/CompetitorAnalysis/Tableau/EmbedSamples/TableauEmbedSamples'));
const TableauMultiTenancy = lazy(() => import('./pages/CompetitorAnalysis/Tableau/MultiTenancy/TableauMultiTenancy'));
const TableauPixelPerfectReporting = lazy(() => import('./pages/CompetitorAnalysis/Tableau/PixelPerfectReporting/TableauPixelPerfectReporting'));
const TableauWhiteLabel = lazy(() => import('./pages/CompetitorAnalysis/Tableau/WhiteLabel/TableauWhiteLabel'));
const CrystalReportsOverview = lazy(() => import('./pages/CompetitorAnalysis/CrystalReports/Overview/CrystalReportsOverview'));
const CrystalReportsArchitectureDeployment = lazy(() => import('./pages/CompetitorAnalysis/CrystalReports/ArchitectureDeployment/CrystalReportsArchitectureDeployment'));
const CrystalReportsEmbeddingApis = lazy(() => import('./pages/CompetitorAnalysis/CrystalReports/EmbeddingApis/CrystalReportsEmbeddingApis'));
const CrystalReportsMultiTenancy = lazy(() => import('./pages/CompetitorAnalysis/CrystalReports/MultiTenancy/CrystalReportsMultiTenancy'));
const CrystalReportsSelfServiceReporting = lazy(() => import('./pages/CompetitorAnalysis/CrystalReports/SelfServiceReporting/CrystalReportsSelfServiceReporting'));
const CrystalReportsReportingExportingCapabilities = lazy(() => import('./pages/CompetitorAnalysis/CrystalReports/ReportingExportingCapabilities/CrystalReportsReportingExportingCapabilities'));
const CrystalReportsSummary = lazy(() => import('./pages/CompetitorAnalysis/CrystalReports/Summary/CrystalReportsSummary'));
const CrystalReportsLiveDemo = lazy(() => import('./pages/CompetitorAnalysis/CrystalReports/LiveDemo/CrystalReportsLiveDemo'));

const getRouterBasename = () => {
  const publicUrl = process.env.PUBLIC_URL || '/';

  if (publicUrl === '/') {
    return undefined;
  }

  try {
    return new URL(publicUrl, window.location.origin).pathname.replace(/\/$/, '') || undefined;
  } catch {
    return publicUrl.replace(/\/$/, '') || undefined;
  }
};

export default function App() {
  const useHashRouter = process.env.REACT_APP_USE_HASH_ROUTER === 'true';
  const basename = getRouterBasename();
  const historyInstance = React.useMemo(
    () =>
      useHashRouter
        ? createHashHistory()          // no basename — hash is the internal route; <base href> covers the real URL prefix
        : createBrowserHistory({ basename }),
    [useHashRouter, basename]
  );

  const content = (
    <Layout>
      <Suspense fallback={<div />}>
        <Switch>
          <Route exact path="/">
            <Landing></Landing>
          </Route>
          <Route path="/discovery/embed-options">
            <MainEmbedOptions productSelected="Composer"></MainEmbedOptions>
          </Route>
          <Route path="/discovery/ux-interactivity">
            <UxInteractivity productSelected="Composer"></UxInteractivity>
          </Route>
          <Route path="/discovery/ui-themes">
            <UiThemes productSelected="Composer"></UiThemes>
          </Route>
          <Route path="/discovery/cross-visual-filtering">
            <CrossVisualFiltering
              productSelected="Composer"
              defaultDashboardName={NAME_OF_DEFAULT_FILTER_DASHBOARD}
            ></CrossVisualFiltering>
          </Route>
          <Route path="/discovery/embed-api">
            <EmbedApi productSelected="Composer"></EmbedApi>
          </Route>
          <Route path="/discovery/event-listeners">
            <EventListeners productSelected="Composer"></EventListeners>
          </Route>
          <Route path="/my-samples">
            <MySamples />
          </Route>
          <Route path="/sample/:id">
            <Sample />
          </Route>
          <Route path="/new-account">
            <NewAccount />
          </Route>
          <Route path="/tableau/embed-samples">
            <TableauEmbedSamples />
          </Route>
          <Route path="/tableau/multi-tenancy">
            <TableauMultiTenancy />
          </Route>
          <Route path="/tableau/pixel-perfect-reporting">
            <TableauPixelPerfectReporting />
          </Route>
          <Route path="/tableau/white-label">
            <TableauWhiteLabel />
          </Route>
          <Route path="/crystal-reports/overview">
            <CrystalReportsOverview />
          </Route>
          <Route path="/crystal-reports/architecture-deployment">
            <CrystalReportsArchitectureDeployment />
          </Route>
          <Route path="/crystal-reports/embedding-apis">
            <CrystalReportsEmbeddingApis />
          </Route>
          <Route path="/crystal-reports/multi-tenancy">
            <CrystalReportsMultiTenancy />
          </Route>
          <Route path="/crystal-reports/self-service-reporting">
            <CrystalReportsSelfServiceReporting />
          </Route>
          <Route path="/crystal-reports/reporting-exporting-capabilities">
            <CrystalReportsReportingExportingCapabilities />
          </Route>
          <Route path="/crystal-reports/summary">
            <CrystalReportsSummary />
          </Route>
          <Route path="/crystal-reports/live-demo">
            <CrystalReportsLiveDemo />
          </Route>
        </Switch>
      </Suspense>
    </Layout>
  );

  return <Router history={historyInstance}>{content}</Router>;
}
